<template lang="pug">
web-app-layout(:showLogo="false" :noPadding="true" title="")
  .collection-detail-page
    // Header with back button and title
    .page-header
      .header-content
        button.back-button(@click="handleBack")
          img(src="@/assets/icons/chevron-right.svg" alt="Back").back-icon
        h1.page-title {{ collectionTitle }}

    // Content
    .page-content
      // Cover image section
      .cover-image-section(v-if="coverImageUrl")
        .cover-image-wrapper
          img(
            :src="coverImageUrl"
            :alt="collectionTitle"
            @error="handleImageError"
          ).cover-image

      // Organizations list
      .organizations-section(v-if="organizations.length > 0")
        .organizations-container
          .organization-list-item(
            v-for="(organization, index) in organizations"
            :key="organization.id || index"
            :class="{ 'has-divider': shouldShowDivider(index) }"
            @click="handleOrganizationClick(organization)"
          )
            // Organization avatar
            .organization-avatar-wrapper
              img(
                v-if="organization.avatar"
                :src="organization.avatar"
                :alt="organization.name"
                @error="handleOrgImageError"
              ).organization-avatar
              img(
                v-else
                src="@/assets/images/default-profile.jpeg"
              ).organization-avatar

            // Organization info
            .organization-info
              .organization-name {{ organization.name }}
              .organization-ein(v-if="organization.einNumber || organization.benevityId")
                | EIN: {{ organization.einNumber || organization.benevityId }}

            // Chevron icon
            .chevron-icon
              img(src="@/assets/icons/chevron-right.svg" alt="View")

      // Empty state
      .empty-state(v-else)
        .empty-state-content
          img(src="@/assets/icons/searchIcon.svg" alt="No organizations").empty-icon
          p.empty-message No organizations found in this collection
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted, onUnmounted,
} from 'vue';
import { useRouter } from 'vue-router';
import WebAppLayout from './dashboard/WebAppLayout.vue';

export default defineComponent({
  name: 'CollectionDetail',
  components: {
    WebAppLayout,
  },
  setup() {
    const router = useRouter();

    // State
    const collectionTitle = ref('Collection');
    const coverImageUrl = ref('');
    const organizations = ref<any[]>([]);

    // Computed
    const shouldShowDivider = computed(() => (index: number): boolean => {
      const totalOrgs = organizations.value.length;
      return index < totalOrgs - 1;
    });

    // Methods
    const handleBack = () => {
      // Clean up sessionStorage when navigating back
      try {
        sessionStorage.removeItem('collectionGroup');
      } catch (e) {
        console.error('Failed to clear collection data:', e);
      }
      router.back();
    };

    const handleOrganizationClick = (organization: any) => {
      if (!organization.id) return;

      // Navigate to organization public profile
      // Use slug if available, otherwise use id
      if (organization.slug) {
        router.push(`/web/org/${organization.slug}`);
      } else {
        router.push(`/web/profile/organization/${organization.id}`);
      }
    };

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      target.style.display = 'none';
      console.error('Failed to load cover image:', target.src);
    };

    const handleOrgImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      target.src = '/src/assets/images/default-profile.jpeg';
      console.error('Failed to load organization avatar:', target.src);
    };

    // Lifecycle
    onMounted(() => {
      // Try to get collection data from sessionStorage
      try {
        const storedData = sessionStorage.getItem('collectionGroup');
        if (storedData) {
          const group = JSON.parse(storedData);
          collectionTitle.value = group.title || 'Collection';
          coverImageUrl.value = group.coverImageUrl || '';
          organizations.value = group.organizations || [];
          return;
        }
      } catch (e) {
        console.error('Failed to parse collection data:', e);
      }

      // No collection data found, redirect to home
      router.push('/web/');
    });

    onUnmounted(() => {
      // Clean up sessionStorage when component is destroyed
      try {
        sessionStorage.removeItem('collectionGroup');
      } catch (e) {
        console.error('Failed to clear collection data:', e);
      }
    });

    return {
      collectionTitle,
      coverImageUrl,
      organizations,
      shouldShowDivider,
      handleBack,
      handleOrganizationClick,
      handleImageError,
      handleOrgImageError,
    };
  },
});
</script>

<style lang="scss" scoped>
.collection-detail-page {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.page-header {
  padding: 0 var(--web-app-padding-mobile) 1rem;

  @media (min-width: 768px) {
    padding: 0 var(--web-app-padding-tablet) 1.25rem;
  }
  
  @media (min-width: 1024px) {
    padding: 0 var(--web-app-padding-desktop) 1.25rem;
  }
}

.header-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (min-width: 768px) {
    gap: 1rem;
  }
}

.back-button {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }

  .back-icon {
    width: 1.5rem;
    height: 1.5rem;
    transform: rotate(180deg); // Flip chevron-right to point left
  }
}

.page-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: #000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
}

.page-content {
  padding: 0 var(--web-app-padding-mobile) 1.5rem;

  @media (min-width: 768px) {
    padding: 0 var(--web-app-padding-tablet) 2rem;
  }
  
  @media (min-width: 1024px) {
    padding: 0 var(--web-app-padding-desktop) 2rem;
  }
}

.cover-image-section {
  margin-bottom: 1.5rem;
}

.cover-image-wrapper {
  width: 100%;
  height: 12.5rem; // 200px
  border-radius: 0.75rem;
  overflow: hidden;
  background-color: #e9ecef;

  @media (min-width: 768px) {
    height: 15rem; // 240px
    border-radius: 1rem;
  }
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.organizations-section {
  margin-bottom: 1.5rem;
}

.organizations-container {
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.75rem;
  overflow: hidden;

  @media (min-width: 768px) {
    border-radius: 1rem;
  }
}

.organization-list-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  background-color: #fff;

  @media (min-width: 768px) {
    padding: 1.125rem 1.25rem;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &.has-divider::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 4.5rem;
    right: 0;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.organization-avatar-wrapper {
  flex-shrink: 0;
  margin-right: 0.75rem;

  @media (min-width: 768px) {
    margin-right: 1rem;
  }
}

.organization-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  object-fit: cover;
  display: block;

  @media (min-width: 768px) {
    width: 2.75rem;
    height: 2.75rem;
  }
}

.organization-info {
  flex: 1;
  min-width: 0;
  margin-right: 0.75rem;
}

.organization-name {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #000;
  margin-bottom: 0.125rem;

  @media (min-width: 768px) {
    font-size: 1.0625rem;
  }
}

.organization-ein {
  font-size: 0.875rem;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chevron-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;

  img {
    width: 1.25rem;
    height: 1.25rem;
    opacity: 0.6;
  }
}

.empty-state {
  padding: 3rem 1rem;
  text-align: center;
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.empty-icon {
  width: 3rem;
  height: 3rem;
  opacity: 0.4;
}

.empty-message {
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
}
</style>
